/**
 * useFinopsTrend — hook de dados para tendência de custo FinOps.
 *
 * Despacha para o endpoint correto de acordo com `scope`:
 *   - 'platform' → getTrend         (GET /api/v1/finops/trend)
 *   - 'tenant'   → getTenantTrend   (GET /api/v1/tenants/{id}/finops/trend)
 *
 * Para `scope='tenant'`, `tenantId` é obrigatório; o hook fica desabilitado
 * (`enabled: false`) enquanto `tenantId` for undefined.
 *
 * staleTime:
 *   - 60 s  quando o período inclui o mês corrente (janela parcial — dados inacabados)
 *   - 1 h   quando a janela é completamente fechada
 *
 * Chave de cache: ['finops', 'trend', scope, tenantId|null, start, end, granularity]
 */
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { getTrend, getTenantTrend } from '@/api/finopsDashboard'
import type { TrendResponse } from '@/api/finopsDashboard'

export type TrendGranularity = 'DAILY' | 'MONTHLY'

export interface UseFinopsTrendParams {
  start: string                          // YYYY-MM-DD inclusivo
  end: string                            // YYYY-MM-DD exclusivo
  granularity: TrendGranularity
  scope: 'platform' | 'tenant'
  tenantId?: number                      // obrigatório quando scope='tenant'
}

/** Retorna true se a janela [start, end) inclui o mês corrente (período parcial). */
function isPeriodPartial(end: string): boolean {
  const curMonthStart = dayjs().startOf('month')
  return dayjs(end).isAfter(curMonthStart)
}

const STALE_PARTIAL = 60_000
const STALE_CLOSED  = 3_600_000

export function useFinopsTrend({
  start,
  end,
  granularity,
  scope,
  tenantId,
}: UseFinopsTrendParams) {
  const staleTime = isPeriodPartial(end) ? STALE_PARTIAL : STALE_CLOSED

  // Para escopo tenant, desabilita enquanto tenantId não estiver disponível
  const enabled = scope === 'tenant' ? typeof tenantId === 'number' : true

  return useQuery<TrendResponse>({
    queryKey: [
      'finops',
      'trend',
      scope,
      tenantId ?? null,
      start,
      end,
      granularity,
    ],
    queryFn: () => {
      const period = { start, end }

      if (scope === 'tenant') {
        return getTenantTrend(tenantId!, period, granularity)
      }
      return getTrend(period, granularity)
    },
    staleTime,
    enabled,
  })
}
